import React, { FC, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';
import { IBaseComponent } from '../../sharedInterfaces/IBaseComponent';
import { IState } from '~/sharedInterfaces/IState';
import ComponentError from '../ComponentError/ComponentError';
import { setDocumentUploadFiles } from '~/actions/formActions';
import { IDocumentUploadSettings } from '~/sharedInterfaces/SharedInterfaces';
import { getInputClassNames, hashCode } from '~/utils';
import { logUserFirstInteraction } from '~/actions/widgetContextActions';
import Tooltip from '../Base/Tooltip/Tooltip';

// TODO:
// - Possibly read orgNumber from query parameter, in case of redirect between step 1 & 2?
// - Possibly separate state between step one and two
// - What happens when a user tries to upload a big file on bad connection, do we disable the submiting of step two

export interface IDocumentUpload {}

interface IDocumentUploadProps extends IBaseComponent {
  tooltip?: boolean;
}

const formatSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  const kb = Math.round(size / 1024);
  if (kb < 1024) return `${kb} KiB`;
  const mb = Math.round(kb / 1024);
  return `${mb} MiB`;
};

const DocumentUpload: FC<IDocumentUploadProps> = ({ componentName, error, tooltip }) => {
  const dispatch = useDispatch();
  const fileList = useSelector((state: IState) => state.onboarding.documentUpload || []);
  const { locale, settings } = useSelector((state: IState) => state.widgetContext);

  const [maxLimitError, setMaxLimitError] = useState(false);
  const componentLabels = getDefaultLabelName(locale, componentName) as IDocumentUploadSettings;
  const uniqueKey = hashCode(`${settings.id}${componentName}`);

  const dropZoneOptions = {
    accept:
      'application/pdf, image/jpeg, image/png, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    maxSize: 31457280,
    maxFiles: 4,
  };

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone(dropZoneOptions);

  useEffect(() => {
    if (acceptedFiles.length > 0 && acceptedFiles.length + fileList.length <= 4) {
      setMaxLimitError(false);
      const newFileListState = acceptedFiles.map((file) => {
        return {
          objectURL: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
        };
      });
      dispatch(logUserFirstInteraction());
      dispatch(setDocumentUploadFiles([...newFileListState, ...fileList]));
    } else if (acceptedFiles.length + fileList.length > 4) {
      setMaxLimitError(true);
    }
  }, [acceptedFiles]);

  useEffect(() => {
    if (maxLimitError) setTimeout(() => setMaxLimitError(false), 5000);
  }, [maxLimitError]);

  const handleRemoveFile = (index: number) => {
    const tempListForRemoveUpdate = [...fileList];
    URL.revokeObjectURL(tempListForRemoveUpdate[index].objectURL);
    tempListForRemoveUpdate.splice(index, 1);
    dispatch(setDocumentUploadFiles(tempListForRemoveUpdate));
  };

  const uploadedDocumentsClassNames = ['qred-first', 'qred-second', 'qred-third', 'qred-fourth'];

  return (
    <div
      className={getInputClassNames({
        tooltip,
        error,
        componentName,
      })}
    >
      {componentLabels.tooltipText && (
        <Tooltip tooltip={tooltip} tooltipText={componentLabels.tooltipText} />
      )}
      <div className="qred-group">
        <div className="qred-row">
          <label htmlFor={uniqueKey}>{componentLabels.title}</label>
          <div
            {...getRootProps({
              className: `qred-dropzone`,
            })}
          >
            <input {...getInputProps()} key={uniqueKey} id={uniqueKey} name={uniqueKey} />
          </div>
          {!isDragActive && (
            <p className="qred-first">
              <span className="qred-first">{componentLabels.dropzoneText}</span>
            </p>
          )}
          {isDragAccept && (
            <p className="qred-first">
              <span className="qred-first">{componentLabels.dragAcceptText}</span>
            </p>
          )}
          {isDragReject && (
            <p className="qred-first">
              <span className="qred-first">{componentLabels.dragRejectText}</span>
            </p>
          )}
          {maxLimitError && <ComponentError error={componentLabels.tooManyFilesText} />}
          {fileList.length > 0 && (
            <ul className="qred-uploads">
              {fileList.map((file: any, i: number) => (
                <li key={file.objectURL}>
                  <p className={uploadedDocumentsClassNames[i]}>
                    <span className="first">{file.name}</span>
                    <span className="second">{formatSize(file.size)}</span>
                  </p>
                  <span className="first">
                    <FontAwesomeIcon onClick={() => handleRemoveFile(i)} icon={faTimesCircle} />
                  </span>
                </li>
              ))}
            </ul>
          )}
          {error && <ComponentError error={error} />}
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;
